import { defineStore } from "pinia";
import { ref } from "vue";
import { EnumFlowType } from "nlb-buy-go-library";
import { IKeyValue } from "./app-store";
import { IFileData } from "@/fw4/store/store";

interface IFlowData {
    flowType: EnumFlowType | undefined;
    documentationId: string;
    isAppDataLoading: boolean;
    isViewDataLoading: boolean;
}

interface IVerificationData {
    prefix: IKeyValue | undefined;
    phoneNumber: number | undefined;
    msisdn: string | undefined;
    code: string;
    email: string;
}

interface IEmailVerificationData {
    emailVerificationToken: string;
    orderToken: string;
}

interface IIdentificationData {
    identificationType: string;
    identificationId: string;
    operaterId: string;
    rekonoCode: string;
    errorText: string;
    isDocumentationNeeded: boolean;
    isDocumentationUploaded: boolean;
    isPreapproved: boolean;
    idDocumentImage: IFileData[];
}

interface IDocumentationData {
    documentationFiles: IFileData[];
    isSlovenianCitizen: boolean;
    employmentType: string | undefined;
    shouldShowFinapi: boolean;
}

interface IHeaderData {
    headerTitle: string;
    headerSubtitle: string;
};

interface IPublicStore {
    FlowData: IFlowData;
    Verification: IVerificationData;
    EmailVerification: IEmailVerificationData;
    IdentificationData: IIdentificationData;
    HeaderData: IHeaderData;
    DocumentationData: IDocumentationData;
    PasswordResetData: IPasswordResetData;
    ProcessEndScreenData: IProcessEndScreenData;
}

interface IPasswordResetData {
    newPassword: string | undefined;
    repeatPassword: string | undefined;
    passwordResetId: string | undefined;
}

interface IProcessEndScreenData {
    processEndTitle: string;
    processEndSubtitle: string;
    processEndIcon: string;
}

const defaultVerificationData: IVerificationData = {
    prefix: { Key: "386", Value: "SI +386" },
    phoneNumber: undefined,
    msisdn: undefined,
    code: "",
    email: "",
}

const defaultEmailVerificationData: IEmailVerificationData = {
    emailVerificationToken: "",
    orderToken: ""
}

const defaultIdentificationData: IIdentificationData = {
    identificationType: "",
    identificationId: "",
    operaterId: "",
    rekonoCode: "",
    errorText: "",
    isDocumentationNeeded: false,
    isDocumentationUploaded: false,
    isPreapproved: false,
    idDocumentImage: []
}

const defaultFlowData: IFlowData = {
    flowType: undefined,
    documentationId: "",
    isAppDataLoading: false,
    isViewDataLoading: false,
}

const defaultDocumentationData: IDocumentationData = {
    documentationFiles: [],
    isSlovenianCitizen: false,
    shouldShowFinapi: false,
    employmentType: undefined
}

const defaultHeaderData: IHeaderData = {
    headerTitle: "",
    headerSubtitle: ""
};

const defaultPasswordResetData: IPasswordResetData = {
    newPassword: undefined,
    repeatPassword: undefined,
    passwordResetId: undefined
}

const defaultProcessEndScreenData: IProcessEndScreenData = {
    processEndTitle: "",
    processEndSubtitle: "",
    processEndIcon: ""
}

export const usePublicStore = defineStore("publicStore", () => {
    const PublicState = ref<IPublicStore>({
        Verification: {
            ...defaultVerificationData
        },
        FlowData: {
            ...defaultFlowData
        },
        EmailVerification: {
            ...defaultEmailVerificationData
        },
        IdentificationData: {
            ...defaultIdentificationData
        },
        DocumentationData: {
            ...defaultDocumentationData
        },
        HeaderData: {
            ...defaultHeaderData
        },
        PasswordResetData: {
            ...defaultPasswordResetData
        },
        ProcessEndScreenData: {
            ...defaultProcessEndScreenData
        }
    });

    function reset() {
        PublicState.value.Verification = { ...defaultVerificationData }
        PublicState.value.FlowData = { ...defaultFlowData }
        PublicState.value.DocumentationData = { ...defaultDocumentationData }
        PublicState.value.HeaderData = { ...defaultHeaderData };
        PublicState.value.PasswordResetData = { ...defaultPasswordResetData };
        PublicState.value.ProcessEndScreenData = { ...defaultProcessEndScreenData };
    }
    
    return { PublicState, reset };
});